import { create } from 'zustand';
import { api } from '../services/api';

export interface PantryItem {
  id: string;
  name: string;
  category: string;
  quantity: number;
  unit: string;
  expiry_date?: string;
  location: string;
  // Returned only by POST/PUT/WS payloads; list endpoints omit these.
  family_id?: string;
  notes?: string;
  added_by?: string;
  created_at?: string;
  updated_at?: string;
}

const EXPIRY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const computeExpiring = (items: PantryItem[]): PantryItem[] => {
  const cutoff = Date.now() + EXPIRY_WINDOW_MS;
  return items
    .filter(it => {
      if (!it.expiry_date) return false;
      const t = new Date(it.expiry_date).getTime();
      return Number.isFinite(t) && t <= cutoff;
    })
    .sort((a, b) => new Date(a.expiry_date!).getTime() - new Date(b.expiry_date!).getTime());
};

export interface ReceiptItem {
  name: string;
  category: string;
  quantity: number;
  unit: string;
  location: string;
  selected?: boolean;
}

interface PantryState {
  items: PantryItem[];
  expiringItems: PantryItem[];
  isLoading: boolean;
  isScanning: boolean;
  error: string | null;
  receiptItems: ReceiptItem[];
  fetchItems: (filters?: { location?: string; category?: string }) => Promise<void>;
  fetchExpiring: () => Promise<void>;
  addItem: (data: Partial<PantryItem>) => Promise<void>;
  updateItem: (id: string, data: Partial<PantryItem>) => Promise<void>;
  deleteItem: (id: string) => Promise<void>;
  scanReceipt: (image: string, mimeType: string) => Promise<void>;
  addBatchItems: (items: ReceiptItem[]) => Promise<void>;
  clearReceiptItems: () => void;
  handleWsMessage: (message: any) => void;
}

export const usePantryStore = create<PantryState>((set, get) => ({
  items: [],
  expiringItems: [],
  isLoading: false,
  isScanning: false,
  error: null,
  receiptItems: [],

  fetchItems: async (filters) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (filters?.location) params.append('location', filters.location);
      if (filters?.category) params.append('category', filters.category);
      const query = params.toString();
      const res = await api.get(`/pantry${query ? `?${query}` : ''}`);
      const items: PantryItem[] = res.data.items;
      // Unfiltered fetch covers every item, so derive expiring from it and
      // skip the separate /pantry/expiring round trip. Filtered fetches leave
      // expiringItems as-is (they only hold a subset).
      const isFullFetch = !filters || (!filters.location && !filters.category);
      set(state => ({
        items,
        isLoading: false,
        expiringItems: isFullFetch ? computeExpiring(items) : state.expiringItems,
      }));
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  // Derives expiringItems from the current items list. Callers used to hit
  // /api/pantry/expiring here; we now piggy-back on whatever fetchItems loads.
  fetchExpiring: async () => {
    set(state => ({ expiringItems: computeExpiring(state.items) }));
  },

  addItem: async (data) => {
    try {
      const res = await api.post('/pantry', data);
      const newItem = res.data.item;
      set(state => {
        const items = [...state.items, newItem];
        return { items, expiringItems: computeExpiring(items) };
      });
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  updateItem: async (id, data) => {
    try {
      const res = await api.put(`/pantry/${id}`, data);
      const updatedItem = res.data.item;
      set(state => {
        const items = state.items.map(i => i.id === id ? updatedItem : i);
        return { items, expiringItems: computeExpiring(items) };
      });
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  deleteItem: async (id) => {
    try {
      await api.delete(`/pantry/${id}`);
      set(state => {
        const items = state.items.filter(i => i.id !== id);
        return { items, expiringItems: computeExpiring(items) };
      });
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  scanReceipt: async (image, mimeType) => {
    set({ isScanning: true, error: null, receiptItems: [] });
    try {
      const res = await api.post('/pantry/receipt', { image, mimeType }, { timeout: 60000 });
      const items = (res.data.items || []).map((item: ReceiptItem) => ({
        ...item,
        selected: true,
      }));
      set({ receiptItems: items, isScanning: false });
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message;
      set({ error: msg, isScanning: false });
      throw new Error(msg);
    }
  },

  addBatchItems: async (items) => {
    try {
      const res = await api.post('/pantry/batch', { items });
      const addedItems = res.data.items || [];
      set(state => {
        const next = [...state.items, ...addedItems];
        return { items: next, expiringItems: computeExpiring(next), receiptItems: [] };
      });
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  clearReceiptItems: () => {
    set({ receiptItems: [], error: null });
  },

  handleWsMessage: (message) => {
    switch (message.type) {
      case 'PANTRY_ITEM_ADDED':
        set(state => {
          if (state.items.find(i => i.id === message.payload.id)) return state;
          const items = [...state.items, message.payload];
          return { items, expiringItems: computeExpiring(items) };
        });
        break;

      case 'PANTRY_ITEM_UPDATED':
        set(state => {
          const items = state.items.map(i => i.id === message.payload.id ? { ...i, ...message.payload } : i);
          return { items, expiringItems: computeExpiring(items) };
        });
        break;

      case 'PANTRY_ITEM_DELETED':
        set(state => {
          const items = state.items.filter(i => i.id !== message.payload.id);
          return { items, expiringItems: computeExpiring(items) };
        });
        break;
    }
  },
}));
